import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Peak-hour heatmap — 7 days × 24 hours grid of join counts.
 * Color intensity = cell.count / maxCount (linear scale, clamped).
 *
 * Props:
 *   data: { timezone: string, maxCount: number, cells: [{dayOfWeek, hour, count}] }
 *   dayNames: array of 7 localized short day names (Sun..Sat)
 */
export default function Heatmap({ data, dayNames }) {
  const { t } = useTranslation();

  const matrix = useMemo(() => {
    const m = Array.from({ length: 7 }, () => new Array(24).fill(0));
    if (!data?.cells) return m;
    for (const c of data.cells) {
      if (c.dayOfWeek >= 0 && c.dayOfWeek < 7 && c.hour >= 0 && c.hour < 24) {
        m[c.dayOfWeek][c.hour] = c.count || 0;
      }
    }
    return m;
  }, [data]);

  const max = data?.maxCount || 0;

  const colorFor = (count) => {
    if (!count || !max) return 'rgba(255,255,255,0.04)';
    // Ramp from transparent -> violet. Floor at 8% so small values are still visible.
    const ratio = Math.max(0.08, Math.min(1, count / max));
    return `rgba(124, 58, 237, ${ratio})`;
  };

  return (
    <div style={wrapStyle} role="table" aria-label={t('analytics.heatmap.ariaTable')}>
      <div style={headerRowStyle}>
        <div style={corner} />
        {Array.from({ length: 24 }, (_, h) => (
          <div key={h} style={hourCellStyle} role="columnheader">
            {h % 3 === 0 ? String(h).padStart(2, '0') : ''}
          </div>
        ))}
      </div>
      {matrix.map((row, dow) => (
        <div key={dow} style={dayRowStyle} role="row">
          <div style={dayLabelStyle} role="rowheader">{dayNames[dow]}</div>
          {row.map((count, h) => (
            <div
              key={h}
              role="cell"
              style={{ ...cellStyle, background: colorFor(count) }}
              title={t('analytics.heatmap.cellTooltip', {
                day: dayNames[dow],
                hour: String(h).padStart(2, '0'),
                count,
              })}
            >
              {count > 0 && max > 0 && count / max >= 0.5 ? count : ''}
            </div>
          ))}
        </div>
      ))}
      <div style={legendStyle}>
        <span style={legendLabelStyle}>{t('analytics.heatmap.fewer')}</span>
        {[0.1, 0.25, 0.5, 0.75, 1.0].map((r) => (
          <div
            key={r}
            style={{ ...legendCellStyle, background: `rgba(124, 58, 237, ${r})` }}
          />
        ))}
        <span style={legendLabelStyle}>{t('analytics.heatmap.more')}</span>
        {data?.timezone && (
          <span style={legendTzStyle}>
            {t('analytics.heatmap.timezone', { tz: data.timezone })}
          </span>
        )}
      </div>
    </div>
  );
}

const wrapStyle = {
  display: 'flex', flexDirection: 'column', gap: 2,
  overflowX: 'auto',
};
const headerRowStyle = {
  display: 'grid',
  gridTemplateColumns: '64px repeat(24, minmax(22px, 1fr))',
  gap: 2,
  minWidth: 680,
};
const dayRowStyle = {
  display: 'grid',
  gridTemplateColumns: '64px repeat(24, minmax(22px, 1fr))',
  gap: 2,
  minWidth: 680,
};
const corner = { width: 64 };
const dayLabelStyle = {
  width: 64,
  fontSize: '0.72rem',
  color: '#94A3B8',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  padding: '0 8px',
};
const hourCellStyle = {
  height: 18,
  fontSize: '0.65rem',
  color: '#64748B',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const cellStyle = {
  height: 24,
  borderRadius: 3,
  border: '1px solid rgba(51, 65, 85, 0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.62rem',
  color: '#fff',
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  minWidth: 22,
};
const legendStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  marginTop: 12,
  fontSize: '0.72rem',
  color: '#94A3B8',
};
const legendLabelStyle = {
  padding: '0 6px',
};
const legendCellStyle = {
  width: 18,
  height: 10,
  borderRadius: 2,
  border: '1px solid rgba(51, 65, 85, 0.4)',
};
const legendTzStyle = {
  marginLeft: 'auto',
  fontStyle: 'italic',
  color: '#64748B',
};
